/** <product-grid-slider>: dresses the desktop horizontal slider of the product grid section (arrows, progress bar, end-of-travel disabling) while the scrolling itself stays NATIVE, so it degrades gracefully without JS; wrapped in a guarded IIFE since the section can load it several times. */
(() => {
  if (customElements.get("product-grid-slider")) return;

  class ProductGridSlider extends HTMLElement {
    #ticking = false;
    // Last known track width: the ResizeObserver also fires on HEIGHT changes (every card image that loads grows it), so widths are compared to ignore those and avoid a burst of reflows.
    #lastWidth = -1;

    connectedCallback() {
      this.track = this.querySelector("[data-slider-track]");
      this.progress = this.querySelector("[data-slider-progress]");
      this.prevBtn = this.querySelector("[data-slider-prev]");
      this.nextBtn = this.querySelector("[data-slider-next]");
      if (!this.track) return;

      this.track.addEventListener("scroll", this.#onScroll, { passive: true });
      this.prevBtn?.addEventListener("click", this.#prev);
      this.nextBtn?.addEventListener("click", this.#next);

      // Recomputed when the WIDTH changes (resize, layout); height-only changes are ignored in #onResize to avoid a useless reflow.
      this.#lastWidth = this.track.clientWidth;
      this.resizeObserver = new ResizeObserver(this.#onResize);
      this.resizeObserver.observe(this.track);

      this.#update();
    }

    disconnectedCallback() {
      this.track?.removeEventListener("scroll", this.#onScroll);
      this.prevBtn?.removeEventListener("click", this.#prev);
      this.nextBtn?.removeEventListener("click", this.#next);
      this.resizeObserver?.disconnect();
    }

    // Recalcul throttlé sur une frame : scroll ET resize passent par ici, donc
    // au plus un #update (un seul reflow) par frame, même en rafale d'events.
    #scheduleUpdate() {
      if (this.#ticking) return;
      this.#ticking = true;
      requestAnimationFrame(() => {
        this.#ticking = false;
        this.#update();
      });
    }

    #onScroll = () => this.#scheduleUpdate();

    // The ResizeObserver fires on every track size change, height included, so `contentRect` (already computed, hence no reflow) is read and the recompute only happens when the width really changed.
    #onResize = (entries) => {
      const width = entries[entries.length - 1].contentRect.width;
      if (width === this.#lastWidth) return;
      this.#lastWidth = width;
      this.#scheduleUpdate();
    };

    #prev = () => this.#step(-1);
    #next = () => this.#step(1);

    /** Avance/recule d'exactement une carte, aligné au bord de carte. */
    #step(direction) {
      const cards = this.track.children;
      if (cards.length < 2) {
        this.track.scrollBy({ left: this.track.clientWidth * direction, behavior: "smooth" });
        return;
      }
      // Pas d'une carte = écart entre deux cartes (largeur + gap) : une
      // différence d'offsets, donc indépendante de l'offsetParent.
      const stride = cards[1].offsetLeft - cards[0].offsetLeft;
      if (stride <= 0) return;
      // Offset left for slides 2 and up (the CSS scroll-padding): the target aligns on it to stay consistent with drag snapping, card 0 clamping back to 0.
      const pad = parseFloat(getComputedStyle(this.track).scrollPaddingLeft) || 0;
      // Bord de carte immédiatement suivant / précédent (epsilon pour absorber
      // les arrondis quand on est déjà pile sur un bord).
      const index = (this.track.scrollLeft + pad) / stride;
      const targetIndex =
        direction > 0 ? Math.floor(index + 0.02) + 1 : Math.ceil(index - 0.02) - 1;
      this.track.scrollTo({ left: targetIndex * stride - pad, behavior: "smooth" });
    }

    #update = () => {
      const { scrollLeft, scrollWidth, clientWidth } = this.track;
      const max = scrollWidth - clientWidth;

      // Pas de débordement : slider inutile, on masque les contrôles (CSS).
      this.classList.toggle("is-static", max <= 1);

      // Barre de progression : largeur du pouce = proportion visible ;
      // position (`left`, en % du rail) = progression du scroll.
      if (this.progress) {
        const thumbPct = scrollWidth > 0 ? Math.min(100, (clientWidth / scrollWidth) * 100) : 100;
        const scrolled = max > 0 ? scrollLeft / max : 0;
        this.progress.style.width = `${thumbPct}%`;
        this.progress.style.left = `${scrolled * (100 - thumbPct)}%`;
      }

      // Flèches désactivées en butée (tolérance 1px pour les arrondis).
      this.#toggle(this.prevBtn, scrollLeft <= 1);
      this.#toggle(this.nextBtn, max <= 1 || scrollLeft >= max - 1);
    };

    #toggle(btn, disabled) {
      if (!btn) return;
      btn.disabled = disabled;
      btn.setAttribute("aria-disabled", disabled ? "true" : "false");
    }
  }

  customElements.define("product-grid-slider", ProductGridSlider);
})();
